import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Row, Col } from 'antd';
import { lowerCase, isNil, upperCase, toUpper } from 'lodash';
import styled from 'styled-components';
import { HSText } from '@/Components/Common/HSWidget';
import {
  SENSORS,
  DEVICE_STATES,
  DEVICE_CONNECTION_STATUS,
  getRadarOccupancyState,
  SMART_PLUG_POWER_CONSUMPTION_THRESHOLD,
  SENSOR_VARIANTS,
  INCONTINENCE_SENSOR_STATES,
} from '@/constants/device';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import DoorClosedIcon from '@/Components/Common/Icons/door-closed-icon.svg';
import DoorOpenIcon from '@/Components/Common/Icons/door-open-icon.svg';
import OccupancySensorIcon from '@/Components/Common/Icons/occupancy-sensor-state.svg';
import DryIcon from '@/Components/Common/Icons/dry.svg';
import WetIcon from '@/Components/Common/Icons/wet.svg';
import OccupancySensorOccupiedIcon from '@/Components/Common/Icons/occupancy-sensor-occupied.svg';
import OccupancySensorVacantIcon from '@/Components/Common/Icons/occupancy-sensor-vacant.svg';
import PowerInactiveIcon from '@/Components/Common/Icons/power.svg';
import PowerActiveIcon from '@/Components/Common/Icons/power-active.svg';
import SmartPlugOnIcon from '@/Components/Common/Icons/smart-plug-power-on.svg';
import SmartPlugOffIcon from '@/Components/Common/Icons/smart-plug-power-off.svg';
import TimerIcon from '@/Components/Common/Icons/timer-icon.svg';
import FireIcon from '@/Components/Common/Icons/fire.svg';
import { TemperatureIcon, HumidityIcon } from '../Common/Icons';
import { getLightLabel, light, trendIcon } from '@/Components/Common/deviceUtils.jsx';
import { renderWeatherIcon } from '@/Components/Common/deviceUtils';

const Wrapper = styled.div`
  display: flex;
  justify-content: right;
  align-items: center;
  margin-left: 145px;
  .current-state-container {
    width: 100px;
    display: flex;
    flex-direction: column;
    justify-content: left;
    .sensor-state-title {
      width: 120px;
      color: #005d98;
      &.radar-heading {
        display: block;
        min-height: 18px;
        line-height: 18px;
        white-space: nowrap;
      }
      &.radar-secondary-heading {
        position: relative;
        top: -2px;
      }
      &.greyed {
        color: #808080;
      }
      &.smart-plug {
        &.inactive {
          position: relative;
          right: 6px;
          top: 6px;
        }
      }
      &.off {
        color: #005d98;
      }
    }
    .power-icon {
      position: relative;
      top: 3px;
    }
  }
  .sensor-state {
    position: relative;
    right: 9px;
    &.dry,
    &.wet {
      height: 39px;
      width: 39px;
    }
    path {
      fill: #005d98;
    }
    &.occupancy-icon {
      right: 0px;
      top: 3px;
      path {
        stroke: #005d98;
      }
      &.vacant {
        .occupancy-sensor-state_svg__occupied {
          display: none;
        }
      }
      &.occupied {
        .occupancy-sensor-state_svg__vacant {
          display: none;
        }
      }
    }
    &.wet {
      path {
        fill: #005d98;
      }
    }
    &.dry {
      right: 0px;
      top: 3px;
      path {
        fill: #005d98;
      }
    }
  }
  &.radar_sensor {
    .current-state-container {
      min-height: 56px;
      justify-content: flex-start;
    }
  }
  &.cooktop_sensor {
    .current-state-container {
      min-height: 56px;
      justify-content: flex-start;
    }
  }
  .temperature-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 19px;
    height: 51px;
    position: relative;
    left: 8px;

    .content-main-container {
      display: flex;
      flex-direction: column;
      .content-title {
        font-family: 'Euclid Circular A';
        font-weight: 400;
        font-size: 24px;
        line-height: 100%;
        color: #005d98;
        white-space: nowrap;
      }
    }

    .content-value {
      font-family: 'Euclid Circular A';
      font-style: normal;
      font-weight: 400;
      font-size: 24px;
      line-height: 38px;
      text-align: center;
      color: #005d98;
    }
    .grey-small {
      padding-left: 0;
    }
  }
  .temperature-icon,
  .humidity-icon {
    width: 24px;
    height: 24px;
  }

  .humidity-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 19px;
    justify-content: center;
    height: 51px;
    width: 123px;
    position: relative;
    &.offline {
      right: 14px;
    }
    .content-main-container {
      display: flex;
      flex-direction: column;
      gap: 1px;

      .content-title {
        font-family: 'Euclid Circular A';
        font-size: 18px;
        line-height: 100%;
        color: #005d98;
        margin-left: 5px;
        white-space: nowrap;
      }
    }

    .content-value {
      width: 59px;
      font-family: 'Euclid Circular A';
      font-style: normal;
      font-weight: 400;
      font-size: 24px;
      line-height: 35px;
      text-align: center;
      color: #005d98;
    }
    .content-group-container {
      padding-left: 6px;
    }
    .grey-small {
      padding-left: 0;
    }
  }
  .grey-small {
    display: block;
    width: 90px;
  }
  .fire-status-icons {
    .timer-icon {
      position: relative;
      top: -10px;
    }
  }
  .fire-icon-active {
    height: 20px;
    width: 16px;
    flex-shrink: 0;
    path {
      fill: #52c41a;
    }
  }
  .flush-time-container {
    display: flex;
    gap: 3px;
    position: relative;
    right: 1px;
  }
  .light-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 19px;
    width: 160px;
    padding-left: 4px;
    .content-main-container {
      display: flex;
      flex-direction: column;
      gap: 1px;
      .light-content-container {
        display: flex;
        flex-direction: row;
        gap: 4px;
      }
      .content-title {
        font-family: 'Euclid Circular A';
        font-weight: 400;
        font-size: 14px;
        line-height: 100%;
        color: #808080;
      }
    }
    .content-value {
      height: 51px;
      font-family: 'Euclid Circular A';
      font-style: normal;
      font-weight: 400;
      font-size: 28px;
      line-height: 30px;
      display: flex;
      align-items: center;
      text-align: center;
      color: #005d98;
      .superscript {
        vertical-align: super;
        font-size: 15px;
      }
    }
    .content-category {
      font-family: 'Euclid Circular A';
      font-style: normal;
      font-weight: 400;
      font-size: 12px;
      line-height: 30px;
      display: flex;
      align-items: center;
      color: #005d98;
      margin-top: 12px;
      &.offline {
        color: #808080;
      }
    }
    .content-icon {
      width: 36px;
      height: 40px;
    }
    .grey-small {
      padding-left: 0;
    }
  }
  .content-group-container {
    width: 89px;
    height: 51px;
  }
  &.power_usage,
  &.alerts,
  &.flushed_count {
    .current-state-container {
      position: relative;
      left: -4px;
    }
    .grey-small {
      padding-left: 0;
    }
  }

  .metric-block {
    height: auto;
    align-items: flex-start;
  }

  .metric-text {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .metric-value-row {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 36px;
  }
  .grey-small {
    width: auto;
    white-space: nowrap;
    color: #808080;
    font-size: 18px;
    line-height: 100%;
    padding-left: 5px;
  }
  .title-blue {
    width: auto;
    white-space: nowrap;
    color: #005d98;
    font-size: 18px;
    line-height: 100%;
    padding-left: 5px;
  }
  .content-value {
    &.online {
      color: #005d98;
    }
    &.offline {
      color: #808080;
    }
  }
  .trendIcon {
    display: flex;
    align-items: center;
    margin-left: 2px;
  }
  .smart-plug {
    width: 160px !important;
  }
  .power-value {
    font-size: 16px;
  }
  .power-usage-icon {
    &.inactive {
      position: relative;
      right: 13px;
      top: 4px;
    }
    &.active {
      position: relative;
      top: 4px;
    }
    path {
      fill: #1e5d98;
    }
  }
`;

const IncontinenceSensorStateIcon = ({ sensorState }) => {
  if (sensorState === INCONTINENCE_SENSOR_STATES.DRY) {
    return <DryIcon className="sensor-state dry" />;
  }

  if (sensorState === INCONTINENCE_SENSOR_STATES.WET) {
    return <WetIcon className="sensor-state wet" />;
  }

  return '-';
};

IncontinenceSensorStateIcon.propTypes = {
  sensorState: PropTypes.string,
};

export const SensorState = ({
  sensorState,
  deviceType,
  variant,
  powerStatus,
  powerUsage,
  temperature,
  humidity,
  alerts,
  flushedTime,
  flushedCount,
  ambientLight,
  tempTrend,
  weatherId,
  fallEventTime,
  fallsTodayCount,
  currentFallState,
  cookingLastSessionTime,
  cookingSessionActive,
  cookingSessionsCount,
  meta = {},
  sensorStatus,
  pairedStatus,
  connectionless = false,
}) => {
  const trans = useSelector(translator);
  const { title } = meta;
  const isRadarPresenceTile =
    deviceType === SENSORS.SMART_HOME.RADAR_SENSOR &&
    isNil(fallsTodayCount) &&
    isNil(currentFallState);
  const isRadarFallsTile =
    deviceType === SENSORS.SMART_HOME.RADAR_SENSOR && !isNil(fallsTodayCount);
  const isRadarFallStateTile =
    deviceType === SENSORS.SMART_HOME.RADAR_SENSOR && !isNil(currentFallState);
  const isUnpaired = upperCase(pairedStatus) === DEVICE_CONNECTION_STATUS.UNPAIRED;
  const isConnected = upperCase(sensorStatus) === DEVICE_CONNECTION_STATUS.CONNECTED;
  const isOutdoorSensor = deviceType === SENSORS.SMART_HOME.OUTDOOR_TEMPERATURE_HUMIDITY_SENSOR;
  const isOnline = connectionless || (!isUnpaired && (isOutdoorSensor || isConnected));

  return (
    <Wrapper
      className={`sensor-content-container ${deviceType === SENSORS.SMART_HOME.RADAR_SENSOR ? 'radar_sensor' : deviceType === SENSORS.SMART_HOME.COOKTOP_SENSOR ? 'cooktop_sensor' : ''} ${powerUsage ? 'power_usage' : !isNil(alerts) ? 'alerts' : !isNil(flushedCount) ? 'flushed_count' : ''}`}
    >
      <div className="current-state-container">
        {(deviceType === SENSORS.SMART_HOME.DOOR_WINDOW_SENSOR ||
          deviceType === SENSORS.SMART_HOME.OCCUPANCY_SENSOR ||
          isRadarPresenceTile ||
          deviceType === SENSORS.SMART_HOME.COOKTOP_SENSOR) &&
          !isNil(sensorState) && (
            <HSText
              className={`sensor-state-title ${isRadarPresenceTile || deviceType === SENSORS.SMART_HOME.COOKTOP_SENSOR ? 'radar-heading' : ''}`.trim()}
            >
              {sensorState !== '-'
                ? deviceType === SENSORS.SMART_HOME.COOKTOP_SENSOR
                  ? toUpper(sensorState) === 'ACTIVE'
                    ? trans('OCCUPIED')
                    : trans('VACANT')
                  : trans(toUpper(sensorState))
                : '-'}
            </HSText>
          )}
        {deviceType === SENSORS.SMART_HOME.COOKTOP_SENSOR && sensorState && sensorState !== '-' && (
          <>
            {toUpper(sensorState) === 'ACTIVE' ? (
              <OccupancySensorOccupiedIcon className={`sensor-state occupancy-icon occupied`} />
            ) : (
              <OccupancySensorVacantIcon className={`sensor-state occupancy-icon vacant`} />
            )}
          </>
        )}
        {deviceType === SENSORS.SMART_HOME.DOOR_WINDOW_SENSOR &&
          lowerCase(sensorState) === DEVICE_STATES.DOOR_WINDOW_SENSOR.OPEN && (
            <DoorOpenIcon className={`sensor-state ${lowerCase(sensorState)}`} />
          )}
        {deviceType === SENSORS.SMART_HOME.DOOR_WINDOW_SENSOR &&
          lowerCase(sensorState) === DEVICE_STATES.DOOR_WINDOW_SENSOR.CLOSED && (
            <DoorClosedIcon className={`sensor-state ${lowerCase(sensorState)}`} />
          )}
        {(deviceType === SENSORS.SMART_HOME.OCCUPANCY_SENSOR || isRadarPresenceTile) &&
          (variant === SENSOR_VARIANTS.INCONTINENCE ? (
            <IncontinenceSensorStateIcon sensorState={sensorState} />
          ) : (
            <OccupancySensorIcon
              className={`sensor-state occupancy-icon ${
                deviceType === SENSORS.SMART_HOME.RADAR_SENSOR
                  ? lowerCase(getRadarOccupancyState(sensorState))
                  : lowerCase(sensorState)
              }`}
            />
          ))}
        {isRadarFallsTile && (
          <>
            <HSText className="sensor-state-title radar-heading radar-secondary-heading">
              {trans(title)}
            </HSText>
            <Row style={{ marginLeft: '1px' }}>
              <Col className="flush-time-container">
                <HSText className="blue-small">{fallsTodayCount}</HSText>
              </Col>
            </Row>
          </>
        )}
        {isRadarFallStateTile && (
          <>
            <HSText
              className={`sensor-state-title radar-heading radar-secondary-heading ${currentFallState === '-' ? 'greyed' : ''}`.trim()}
            >
              {trans(title)}
            </HSText>
            <Row>
              <Col>
                {currentFallState === '-' ? (
                  <HSText className="grey-small">-</HSText>
                ) : currentFallState === DEVICE_STATES.RADAR_SENSOR.FALL_DETECTED ? (
                  <HSText className="blue-small" style={{ color: '#FF4D4F', fontWeight: 'bold' }}>
                    {trans('FALL_DETECTED_SHORT')}
                  </HSText>
                ) : (
                  <HSText className="blue-small">{trans('FALL_RECOVERED_SHORT')}</HSText>
                )}
              </Col>
            </Row>
            {fallEventTime && fallEventTime !== '-' && (
              <Row>
                <Col className="flush-time-container">
                  <Row>
                    <TimerIcon className="timer-icon" />
                    <HSText className="blue-small">{fallEventTime}</HSText>
                  </Row>
                </Col>
              </Row>
            )}
          </>
        )}
        {deviceType === SENSORS.SMART_HOME.SMART_PLUG && powerStatus && powerStatus !== '-' && (
          <HSText className={`sensor-state-title ${lowerCase(powerStatus)}`}>
            {trans(upperCase(powerStatus))}
          </HSText>
        )}
        {deviceType === SENSORS.SMART_HOME.SMART_PLUG && powerStatus && powerStatus !== '-' && (
          <>
            {lowerCase(powerStatus) === DEVICE_STATES.SMART_PLUG.ON ? (
              <SmartPlugOnIcon className="power-icon" />
            ) : lowerCase(powerStatus) === DEVICE_STATES.SMART_PLUG.OFF ? (
              <SmartPlugOffIcon className="power-icon" />
            ) : null}
          </>
        )}
        {deviceType === SENSORS.SMART_HOME.SMART_PLUG && !isNil(powerUsage) && (
          <>
            <HSText
              className={`sensor-state-title smart-plug ${powerUsage > SMART_PLUG_POWER_CONSUMPTION_THRESHOLD ? 'active' : powerUsage !== '-' ? 'inactive' : ''}`}
            >
              {Number(powerUsage) > SMART_PLUG_POWER_CONSUMPTION_THRESHOLD
                ? trans('ACTIVE')
                : powerUsage !== '-'
                  ? trans('INACTIVE')
                  : ''}
            </HSText>
            <Row className={`power-value-container ${lowerCase(powerStatus)}`}>
              <Col>
                {powerUsage === '-' ? (
                  <HSText className="power-value">-</HSText>
                ) : (
                  <>
                    {Number(powerUsage) > SMART_PLUG_POWER_CONSUMPTION_THRESHOLD ? (
                      <PowerActiveIcon className="power-usage-icon active" />
                    ) : (
                      <PowerInactiveIcon className="power-usage-icon inactive" />
                    )}
                  </>
                )}
              </Col>
            </Row>
          </>
        )}
        {!isNil(temperature) && (
          <div className="temperature-container metric-block">
            <div className="metric-text">
              <HSText className={isOnline ? 'sensor-state-title' : 'grey-small'}>
                {trans(title)}
              </HSText>

              <div className="metric-value-row">
                <TemperatureIcon className="temperature-icon" />
                <div className={`content-value ${isOnline ? 'online' : 'offline'}`}>
                  {temperature}
                </div>
                {tempTrend !== '-' && <div className="trendIcon">{trendIcon(tempTrend)}</div>}
              </div>
            </div>
          </div>
        )}
        {!isNil(humidity) && (
          <div className={`humidity-container metric-block ${isOnline ? '' : 'offline'}`}>
            <div className="metric-text">
              <HSText className={isOnline ? 'sensor-state-title' : 'grey-small'}>
                {trans(title)}
              </HSText>
              <div className="metric-value-row">
                <HumidityIcon className="humidity-icon" />
                <div className={`content-value ${isOnline ? 'online' : 'offline'}`}>{humidity}</div>
              </div>
            </div>
          </div>
        )}
        {!isNil(ambientLight) && (
          <div className="light-container metric-block">
            <div className="metric-text">
              <HSText className={isOnline ? 'sensor-state-title' : 'grey-small'}>
                {trans(title)}
              </HSText>
              <div className="metric-value-row">
                <div className="content-icon">{light(ambientLight)}</div>
                <div className={`content-value ${isOnline ? 'online' : 'offline'}`}>
                  {ambientLight}
                  <span className="superscript">{ambientLight !== '-' && 'lx'}</span>
                </div>
                {ambientLight !== '-' && (
                  <div className={`content-category ${isOnline ? 'online' : 'offline'}`}>
                    ({trans(getLightLabel(ambientLight))})
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {!isNil(weatherId) && (
          <div className="weather-icon-container">{renderWeatherIcon(weatherId)}</div>
        )}
        {deviceType === SENSORS.SMART_HOME.COOKTOP_SENSOR && !isNil(cookingLastSessionTime) && (
          <>
            <HSText
              className={`sensor-state-title radar-heading radar-secondary-heading ${
                cookingLastSessionTime === '-' ? 'greyed' : ''
              }`.trim()}
            >
              {trans('COOKING_SESSION')}
            </HSText>
            {cookingLastSessionTime !== '-' && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap' }}
              >
                {cookingSessionActive ? (
                  <FireIcon className="fire-icon-active" />
                ) : (
                  <TimerIcon className="timer-icon" />
                )}
                <HSText
                  className="blue-small"
                  style={
                    cookingSessionActive ? { color: '#52c41a', fontWeight: 'bold' } : undefined
                  }
                >
                  {cookingLastSessionTime}
                </HSText>
              </div>
            )}
          </>
        )}
        {deviceType === SENSORS.SMART_HOME.COOKTOP_SENSOR && !isNil(cookingSessionsCount) && (
          <>
            <HSText className="sensor-state-title radar-heading radar-secondary-heading">
              {trans(title)}
            </HSText>
            <Row style={{ marginLeft: '1px' }}>
              <Col className="flush-time-container">
                <HSText className="blue-small">{cookingSessionsCount}</HSText>
              </Col>
            </Row>
          </>
        )}
        {deviceType === SENSORS.SMART_HOME.COOKTOP_SENSOR && !isNil(alerts) && (
          <>
            <HSText className="grey-small">{trans(title)}</HSText>
            <Row className="fire-value-container on">
              <Col>
                <HSText
                  className="fire-value"
                  style={alerts ? { color: '#d46b08', fontWeight: 600 } : {}}
                >
                  {alerts ? trans(alerts) : trans('NO_ALERTS_TRIGGERED')}
                </HSText>
              </Col>
            </Row>
          </>
        )}
        {deviceType === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR && flushedTime && (
          <>
            <HSText className="sensor-state-title on">{trans('FLUSHSTARTED')}</HSText>
            <Row>
              <Col className="flush-time-container">
                <Row>
                  <TimerIcon className="timer-icon" />
                  <HSText className="blue-small">{flushedTime}</HSText>
                </Row>
              </Col>
            </Row>
          </>
        )}
        {deviceType === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR && !isNil(flushedCount) && (
          <>
            <HSText className="sensor-state-title on">{trans(title)}</HSText>
            <Row style={{ marginLeft: '1px' }}>
              <Col className="flush-time-container">
                <HSText className="blue-small">{flushedCount}</HSText>
              </Col>
            </Row>
          </>
        )}
      </div>
    </Wrapper>
  );
};

SensorState.propTypes = {
  sensorState: PropTypes.string,
  deviceType: PropTypes.string,
  variant: PropTypes.string,
  powerStatus: PropTypes.string,
  powerUsage: PropTypes.number,
  temperature: PropTypes.string,
  humidity: PropTypes.string,
  alerts: PropTypes.string,
  flushedTime: PropTypes.string,
  meta: PropTypes.object,
  ambientLight: PropTypes.string,
  tempTrend: PropTypes.string,
  outdoorAmbientLight: PropTypes.string,
  weatherId: PropTypes.string,
  fallEventTime: PropTypes.string,
  fallsTodayCount: PropTypes.number,
  currentFallState: PropTypes.string,
  flushedCount: PropTypes.number,
  cookingState: PropTypes.string,
  cookingLastSessionTime: PropTypes.string,
  cookingSessionActive: PropTypes.bool,
  cookingSessionsCount: PropTypes.number,
  sensorStatus: PropTypes.string,
  pairedStatus: PropTypes.string,
  connectionless: PropTypes.bool,
};
